import { FormProps } from '@alphabait/ui/form'
import {
  closestCenter,
  DndContext,
  DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core'
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import cx from 'classnames'
import { CheckCircle2, GripVertical, PlusCircle, Trash2 } from 'lucide-react'
import { useEffect, useState } from 'react'
import { useController } from 'react-hook-form'
import i18n from '../../../i18n'
import { AlphaTypes, CustomRadioGroupInput } from '../custom-types'
import style from './form.module.css'
import { cn } from '@/lib/utils'

// Radio option item type
type RadioOption = {
  label: string
  value: string
}

type CustomRadioGroupProps = {
  field: CustomRadioGroupInput
  formProps: FormProps
}

// Export the component directly
export function CustomRadioGroupComponent({
  field,
  formProps,
}: CustomRadioGroupProps) {
  const {
    field: { onChange, value },
  } = useController({
    control: formProps.control,
    name: field.name,
  })

  const [selectedValues, setSelectedValues] = useState<string[]>(value || [])

  // Update local state when value changes from external source
  useEffect(() => {
    // Ensure value is always an array
    setSelectedValues(Array.isArray(value) ? value : value ? [value] : [])
  }, [value])

  // Handle selection toggle
  const handleSelect = (optionValue: string) => {
    const newSelectedValues = selectedValues.includes(optionValue)
      ? selectedValues.filter((v) => v !== optionValue) // Remove if already selected
      : [...selectedValues, optionValue] // Add if not selected

    setSelectedValues(newSelectedValues)
    onChange(newSelectedValues)
  }

  const options = field?.options ?? []

  return (
    <div className="grid gap-2 pt-4">
      {options?.map((option) => {
        const isSelected = selectedValues.includes(option.value)
        return (
          <div
            key={`${field.name}-${option.value}`}
            className={cx(
              style.radioGroup,
              'cursor-pointer bg-background-light p-3 rounded-md flex items-center',
              isSelected ? 'border border-primary' : 'border border-base-input',
            )}
            data-testid={`${field.uiOptions?.testId ?? field.name}-${
              option.value
            }`}
            onClick={() => handleSelect(option.value)}
          >
            <div className="flex items-center gap-2">
              {isSelected && (
                <CheckCircle2
                  className="h-6 w-6 fill-primary"
                  stroke="white"
                  strokeWidth={2}
                />
              )}
              <input
                type="checkbox"
                value={option.value}
                checked={isSelected}
                className="sr-only border border-base-input"
                id={`${field.name}-${option.value}`}
                data-testid={`${field.uiOptions?.testId ?? field.name}-${
                  option.value
                }-input`}
                onChange={(e) => {
                  // This prevents the onChange from firing twice with the onClick
                  e.stopPropagation()
                  handleSelect(option.value)
                }}
              />
              <span className="font-normal">{option.label}</span>
            </div>
          </div>
        )
      })}
    </div>
  )
}

export default function CustomRadioGroup({
  field,
  formProps,
}: CustomRadioGroupProps) {
  return <CustomRadioGroupComponent field={field} formProps={formProps} />
}

function OptionInput({
  value,
  onChange,
  onBlur,
  onKeyDown,
  placeholder,
}: {
  value: string
  onChange: (value: string) => void
  onBlur?: () => void
  onKeyDown?: (e: React.KeyboardEvent) => void
  placeholder?: string
}) {
  return (
    <input
      type="text"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      onBlur={onBlur}
      onKeyDown={onKeyDown}
      placeholder={placeholder}
      className="w-full px-2 py-2 border border-border ring-1 ring-border rounded-md outline-none focus:ring-0 text-sm"
    />
  )
}

function DraggableOptionInput({
  option,
  updateOption,
  removeOption,
}: {
  index: number
  option: RadioOption
  updateOption: (value: string) => void
  removeOption: () => void
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: option.value })

  const [value, setValue] = useState(option.label)

  useEffect(() => {
    setValue(option.label)
  }, [option.label])

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : undefined,
  }

  return (
    <div className="flex gap-2 items-center">
      <div
        className="cursor-grab touch-none pl-2 "
        {...attributes}
        {...listeners}
      >
        <GripVertical className="h-10 w-4 text-gray-400" />
      </div>
      <div
        ref={setNodeRef}
        style={style}
        className="flex items-center rounded-md h-inherit flex-1"
      >
        <OptionInput
          value={value}
          onChange={setValue}
          onBlur={() => {
            if (value.trim() !== option.label) {
              updateOption(value)
            }
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault()
              if (value.trim() !== option.label) {
                updateOption(value)
              }
            }
          }}
        />
      </div>
      <button
        type="button"
        onClick={removeOption}
        className="px-1 py-2 hover:bg-gray-100 hover:rounded-full"
      >
        <Trash2 className="h-5 w-5 text-gray-400" />
      </button>
    </div>
  )
}

// This component contains the form content that was previously inside the Controller render prop
function RadioOptionsContent({
  value,
  onChange,
}: {
  value: CustomRadioGroupInput['options']
  onChange: (value: CustomRadioGroupInput['options']) => void
}) {
  const [inputValue, setInputValue] = useState('')
  const [options, setOptions] = useState<CustomRadioGroupInput['options']>([])

  useEffect(() => {
    value && setOptions(value)
  }, [value])

  // Add a new option
  const addOption = () => {
    if (!inputValue.trim()) return
    const newOption = { value: inputValue, label: inputValue }
    const newOptions = [...options, newOption]
    setOptions(newOptions)
    onChange(newOptions)
    setInputValue('')
  }

  // Update an existing option
  const updateOption = (index: number, newValue: string) => {
    if (!newValue.trim()) return
    const newOptions = [...options]
    newOptions[index] = {
      ...newOptions[index],
      value: newValue,
      label: newValue,
    }
    setOptions(newOptions)
    onChange(newOptions)
  }

  // Remove an option
  const removeOption = (index: number) => {
    const newOptions = options.filter((_, i) => i !== index)
    setOptions(newOptions)
    onChange(newOptions)
  }

  // Move options for drag and drop
  const moveOption = (dragIndex: number, hoverIndex: number) => {
    const newOptions = arrayMove(options, dragIndex, hoverIndex)
    setOptions(newOptions)
    onChange(newOptions)
  }

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  )

  return (
    <div className="space-y-6 mt-6">
      <div className="text-base">Answers</div>

      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={(event: DragEndEvent) => {
          const { active, over } = event
          if (active.id !== over?.id) {
            const oldIndex = options.findIndex(
              (option) => option.value === active.id,
            )
            const newIndex = options.findIndex(
              (option) => option.value === over?.id,
            )
            moveOption(oldIndex, newIndex)
          }
        }}
      >
        <SortableContext
          items={options.map((option) => option.value)}
          strategy={verticalListSortingStrategy}
        >
          <div className="space-y-2">
            {options.map((option, index) => (
              <DraggableOptionInput
                key={option.value}
                index={index}
                option={option}
                updateOption={(value) => updateOption(index, value)}
                removeOption={() => removeOption(index)}
              />
            ))}

            {/* Always show an empty field at the end */}
            <div className="flex gap-2 pl-2 items-center">
              <div className="py-1 cursor-not-allowed opacity-40">
                <GripVertical className="h-10 w-4 text-gray-400" />
              </div>
              <div className="flex items-center rounded-md flex-1">
                <OptionInput
                  value={inputValue}
                  onChange={setInputValue}
                  placeholder="Add option"
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && inputValue.trim()) {
                      e.preventDefault()
                      addOption()
                    }
                  }}
                />
              </div>
              <button
                type="button"
                onClick={addOption}
                className={cn(
                  'px-1 py-2 hover:bg-gray-100 hover:rounded-full',
                  !inputValue.trim() && 'opacity-30 cursor-not-allowed',
                )}
              >
                <PlusCircle className="h-5 w-5 text-gray-400" />
              </button>
            </div>
          </div>
        </SortableContext>
      </DndContext>
    </div>
  )
}

function RadioOptions({
  field,
  formProps,
}: {
  field: CustomRadioGroupInput
  formProps: FormProps
}) {
  const {
    field: { onChange, value },
  } = useController({
    control: formProps.control,
    name: field.name,
  })

  return <RadioOptionsContent value={value} onChange={onChange} />
}

// eslint-disable-next-line react-refresh/only-export-components
export const getCustomRadioGroupProperties = () =>
  [
    {
      name: 'uiOptions.label',
      type: 'input',
      uiOptions: {
        label: i18n.t('questionnaire_builder.question'),
        inputType: 'text',
      },
      validations: ['required'],
    },
    {
      name: 'options',
      type: 'render',
      validations: ['required'],
      render: (field: CustomRadioGroupInput, formProps: FormProps) => {
        return <RadioOptions field={field} formProps={formProps} />
      },
    },
    {
      name: 'validation.required',
      customControlType: 'switch',
      type: 'custom',
      uiOptions: {
        label: i18n.t('questionnaire_builder.required'),
      },
    },
  ] as AlphaTypes[]

// For backwards compatibility
// eslint-disable-next-line react-refresh/only-export-components
export const customRadioGroupProperties = getCustomRadioGroupProperties()
